import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, authHeader } from '../lib/api'
import { useAuth } from './useAuth'
import type {
  StudentProfile,
  PersonalRecord,
  CreateStudentProfileRequest,
  UpdateStudentProfileRequest,
} from '@tnf-recruit/shared'

export function useStudentProfile(options?: { enabled?: boolean }) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()

  // Fetch the authenticated student's own profile (includes personal records)
  const profile = useQuery({
    queryKey: ['studentProfile'],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/students/profile', authHeader(token))
      return data.data as StudentProfile | null
    },
  })

  // Create or upsert the full profile
  const saveProfile = useMutation({
    mutationFn: async (body: CreateStudentProfileRequest & { isPublic?: boolean }) => {
      const token = await getAccessToken()
      const { data } = await apiClient.post('/students/profile', body, authHeader(token))
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studentProfile'] })
    },
  })

  // Partial update
  const updateProfile = useMutation({
    mutationFn: async (body: UpdateStudentProfileRequest) => {
      const token = await getAccessToken()
      const { data } = await apiClient.patch('/students/profile', body, authHeader(token))
      return data.data
    },
    onSuccess: updated => {
      // Merge the PATCH response straight into the cache instead of
      // invalidating + refetching: invalidation is async, so a caller that
      // clears its own optimistic state as soon as the mutation settles (e.g.
      // the Settings visibility toggle) would briefly render the old cached
      // value again until the background refetch lands.
      queryClient.setQueryData(['studentProfile'], (old: StudentProfile | null | undefined) =>
        old ? { ...old, ...updated } : updated
      )
    },
  })

  // ─── Personal Records ───────────────────────────────────────────────────

  const records = useQuery({
    queryKey: ['personalRecords'],
    queryFn: async () => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/students/records', authHeader(token))
      return data.data as PersonalRecord[]
    },
  })

  const saveRecord = useMutation({
    mutationFn: async (body: {
      eventId: string
      mark: string
      value: number
      date?: Date
      venue?: string
      isVerified?: boolean
      isOutdoor?: boolean
    }) => {
      const token = await getAccessToken()
      const { data } = await apiClient.post('/students/records', body, authHeader(token))
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalRecords'] })
      queryClient.invalidateQueries({ queryKey: ['studentProfile'] })
    },
  })

  const deleteRecord = useMutation({
    mutationFn: async (recordId: string) => {
      const token = await getAccessToken()
      await apiClient.delete(`/students/records/${recordId}`, authHeader(token))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalRecords'] })
      queryClient.invalidateQueries({ queryKey: ['studentProfile'] })
    },
  })

  return {
    profile,
    saveProfile,
    updateProfile,
    records,
    saveRecord,
    deleteRecord,
  }
}
